/**
 * Shared home-page section-ordering helper (OpenSpec: home-content-section-reorder).
 *
 * The public home page has four reorderable "middle" sections between the fixed
 * Hero and Join Us bookends. Their sequence is persisted on the document as
 * `document.sectionOrder`. Both the admin editor and the public renderer derive
 * their sequence through {@link normalizeSectionOrder} so a missing, partial, or
 * malformed value always resolves to a safe, complete order — and the two render
 * paths can never drift apart.
 */

/** Canonical default order of the four movable middle sections. */
export const MIDDLE_SECTIONS = ["mission", "sessions", "projects", "leaders"] as const;

export type MiddleSection = (typeof MIDDLE_SECTIONS)[number];

/**
 * Normalize a raw (possibly missing/partial/dirty) section-order value into
 * exactly the four canonical middle-section keys, each appearing once:
 * keep valid known keys in the given order, drop unknowns/duplicates, then
 * append any missing canonical keys in their default relative order.
 */
export function normalizeSectionOrder(order?: unknown): MiddleSection[] {
  const canonical: readonly string[] = MIDDLE_SECTIONS;
  const seen = new Set<string>();
  const result: MiddleSection[] = [];

  if (Array.isArray(order)) {
    for (const key of order) {
      if (typeof key === "string" && canonical.includes(key) && !seen.has(key)) {
        seen.add(key);
        result.push(key as MiddleSection);
      }
    }
  }

  for (const key of MIDDLE_SECTIONS) {
    if (!seen.has(key)) {
      seen.add(key);
      result.push(key);
    }
  }

  return result;
}
